import { BiPredicate, Nillable, Nullable, Optional, Restify, Value, ValueResolver } from './his-type.utils';

/** Error thrown when a value does not meet expectations. */
export class ValueError extends Error {
    constructor( message: string, public readonly value?: any ) {
        super( message );
        this.name = 'ValueError';
    }
}

type RequirementFn = ( value: any ) => boolean;

// ==================================
// ---- Hard assertion functions ----
// ==================================

/** Ensures that a value is not null or undefined. */
export function requireNonNil<T = unknown>( value: T | null | undefined, message?: string ): T | never {
    return requireValue( value, isNotNil, message || 'value must not be null or undefined' );
}

/** Ensures that a value is not undefined. Allows `null` as a valid value. */
export function requireDefined<T = unknown>( value: T | null | undefined, message?: string ): T | null | never {
    return requireValue( value, isDefined, message || 'value must not be undefined' );
}

/** Ensures that a value is `true`. Can be used to guard for bad parameters with inline assertions. */
export function requireTrue( value: any, message?: string ): true | never {
    return requireValue( value, isTrue, message || 'value must be true' );
}

/** Ensures that a value is `false`. Can be used to guard for bad parameters with inline assertions. */
export function requireFalse( value: any, message?: string ): false | never {
    return requireValue( value, isFalse, message || 'value must be false' );
}

/**
 * Ensures that a value meets a specified requirement.
 * @param value The value to check.
 * @param requirement A **type guard** function that defines the requirement.
 * @param message An optional error message to throw if the requirement is not met.
 * @returns The value if it meets the requirement.
 * @throws {ValueError} If the value does not meet the requirement.
 */
export function requireValue<T, G extends T | null | undefined>(
    value: T | null | undefined,
    requirement: ( value: T | null | undefined ) => value is G,
    message?: string
): G | never;
/**
 * Ensures that a value meets a specified requirement.
 * @param value The value to check.
 * @param requirement A predicate function that defines the requirement.
 * @param message An optional error message to throw if the requirement is not met.
 * @returns The value if it meets the requirement.
 * @throws {ValueError} If the value does not meet the requirement.
 */
export function requireValue<T>( value: T | null | undefined, requirement: RequirementFn, message?: string ): T | never;
export function requireValue<T = unknown>( value: T | null | undefined, requirement: RequirementFn, message?: string ): T | never {
    if ( !requirement( value ) ) {
        throw new ValueError( message || `value '${value}' does not meet the required condition`, value );
    }
    return value;
}

// ================================
// ---- Higher-order functions ----
// ================================

export function allowNil<T = unknown>( requirement: RequirementFn ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => value === null || value === undefined ? true : requirement( value );
}

export function allowNull<T = unknown>( requirement: RequirementFn ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => value === null ? true : requirement( value );
}

export function allowUndefined<T = unknown>( requirement: RequirementFn ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => value === undefined ? true : requirement( value );
}

// ===================================
// ---- Conditional orchestration ----
// ===================================

export function and<T = unknown>( ...requirements: RequirementFn[] ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => requirements.every( req => req( value ) );
}

export function or<T = unknown>( ...requirements: RequirementFn[] ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => requirements.some( req => req( value ) );
}

export function none<T = unknown>( ...requirements: RequirementFn[] ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => requirements.every( req => !req( value ) );
}

export function not<T = unknown>( requirement: RequirementFn ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => !requirement( value );
}

export function xor<T = unknown>( requirementA: RequirementFn, requirementB: RequirementFn ): ( value: T | null | undefined ) => boolean {
    return ( value: any ) => requirementA( value ) !== requirementB( value );
}

// ==================================
// ---- Soft assertion functions ----
// ==================================

export function isNil( value: any ): value is null | undefined {
    return value === null || value === undefined;
}

export function isNotNil<T = unknown>( value: T | null | undefined ): value is T {
    return !isNil( value );
}

export function isDefined<T = unknown>( value: T | null | undefined ): value is T | null {
    return value !== undefined;
}

export function isNotDefined( value: any ): value is undefined {
    return value === undefined;
}

export function isNull( value: any ): value is null {
    return value === null;
}

export function isNotNull<T = unknown>( value: T | null | undefined ): value is T | undefined {
    return value !== null;
}

export function isTrue( value: any ): value is true {
    return value === true;
}

export function isTruthy( value: any ): boolean {
    return !!value;
}

export function isFalse( value: any ): value is false {
    return value === false;
}

export function isFalsy( value: any ): boolean {
    return !value;
}

// =====================================
// ---- Comparison helper functions ----
// =====================================

/**
 * Provides a set of common functions for comparing values and objects based on equality, loose equality, and specific properties.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const CompareBy = {
    /** Provides a function for strict equality comparison. (`a === b`) */
    equality( a: any, b: any ): boolean {
        return a === b;
    },
    /** Provides a function for loose equality comparison. (`a == b`) */
    looseEquality( a: any, b: any ): boolean {
        return a == b;
    },
    /**
     * Creates a comparison function that compares equality of two objects based on a specific property. The comparison can be customized with an optional equality function for the property values.
     * @param propertyName The name of the property to compare.
     * @param equalityFn An optional function to compare the property values. Defaults to strict equality (`a[propertyName] === b[propertyName]`).
     * @returns A comparison function that compares equality of two objects based on the specified property.
     */
    property<A, B = A>( propertyName: keyof A & keyof B, equalityFn: BiPredicate<A[typeof propertyName], B[typeof propertyName]> = ( a: any, b: any ) => a === b ): BiPredicate<A, B> {
        return ( a: A, b: B ) => equalityFn( a[propertyName], b[propertyName] );
    },
    /** Creates a comparison function that compares equality of two objects based on multiple properties using strict equality (`a[propertyName] === b[propertyName]`). */
    properties<T>( ...propertyNames: ( keyof T )[] ): BiPredicate<T> {
        return ( a: T, b: T ) => ( propertyNames as ( keyof T )[] ).every( prop => a[prop] === b[prop] );
    }
};

// ==================================
// ---- Value resolver functions ----
// ==================================

/**
 * Resolves a value either directly or through a value resolver function.
 * @param valueResolver The value or function that resolves the value.
 * @param defaultValue The default value to return if the value resolver (or its returned value) is undefined. **Note:** `null` is considered a valid value and will be returned as-is.
 * @param ctx The context to pass to the value resolver function if it is a function.
 * @returns The resolved value.
 */
export function resolveValue<V extends Nullable<Value>, Ctx extends Value | unknown = unknown>( valueResolver: Optional<ValueResolver<V, Ctx>>, defaultValue: V, ...ctx: Restify<Ctx> ): V {
    if ( valueResolver === undefined ) {
        return defaultValue;
    }
    if ( typeof valueResolver !== 'function' ) {
        return valueResolver;
    }
    const value = valueResolver( ...ctx );
    return value === undefined ? defaultValue : value;
}

/**
 * Resolves an optional value either directly or through a value resolver function. Returns `undefined` if the value resolver is (or returns) undefined.\
 * **Note:** `null` is considered a valid value and will be returned as-is.
 * @param valueResolver The value or function that resolves the value.
 * @param ctx The context to pass to the value resolver function if it is a function.
 * @returns The resolved value or `undefined`.
 */
export function resolveOptionalValue<V extends Nillable<Value>, Ctx extends Value | unknown = unknown>( valueResolver: Optional<ValueResolver<V, Ctx>>, ...ctx: Restify<Ctx> ): Optional<V> {
    if ( valueResolver === undefined ) {
        return undefined;
    }
    return typeof valueResolver === 'function' ? valueResolver( ...ctx ) : valueResolver;
}